import { RECURRENCE_INTERVALS } from "@gladis/shared";
import { activeLocale } from "@gladis/ui/i18n";

/**
 * How the recurring-expenses table is ordered, when the reader has asked for something other than the
 * order the server sent.
 *
 * ==========================================================================================
 * IT SORTS IN THE BROWSER, AND THAT IS NOT A SHORTCUT
 * ==========================================================================================
 *
 * `GET /api/expenses` returns the household's whole active set in one response — there is no
 * pagination and no intention of adding any, because a household has tens of recurring expenses and
 * not thousands. Every row a sort could reach is already in memory, so a `?sort=` parameter would buy
 * a round trip and a cache key per ordering to rearrange an array the client is holding.
 *
 * The consequence is the one worth naming: THE TOTALS DO NOT MOVE. Sorting is the only control on
 * this page that changes nothing about which rows are counted, so unlike the scope filter it needs no
 * sentence anywhere saying what it did.
 *
 * ==========================================================================================
 * A PURE MODULE, DELIBERATELY
 * ==========================================================================================
 *
 * No React and no JSX, so it is `.ts` and lands inside `vitest.config.ts`'s `src/**\/*.test.ts` — the
 * package's pure-unit layer. The interval ordering is the part that has a wrong answer which looks
 * right, and it is worth being able to assert without rendering a table.
 */

/** The three columns the table offers. Not every column can be ordered meaningfully; these can. */
export type ExpenseSortColumn = "description" | "interval" | "amount" | "nextDue";

export interface ExpenseSort {
  column: ExpenseSortColumn;
  direction: "asc" | "desc";
}

/** Only the fields the comparators read, so a row DTO can grow without touching this. */
export interface SortableExpenseRow {
  description: string;
  interval: string;
  amountInEuros: number;
}

/**
 * A group row sorts on IDENTICAL TERMS, and needs no special case anywhere below.
 *
 * Its `interval` is genuinely `monthly` — a group's figure IS a monthly equivalent by construction,
 * so the word describes the number printed beside it — and its `amountInEuros` is that figure. The
 * only thing it lacks is a `description`, and its label is what the reader sees in that column.
 *
 * That is the whole of this file's 023 delta: one projection, no NULLS-LAST rule, no third sort
 * state, and no comparator changed. Had the interval been treated as ABSENT — the obvious reading —
 * every one of those would have been needed, and the sort would have contradicted the cell the
 * reader is looking at.
 */
export interface SortableGroupRow {
  kind: "group";
  label: string;
  interval: "monthly";
  amountInEuros: number;
}

/** The two row kinds as one comparable shape. */
function sortable(row: SortableExpenseRow | SortableGroupRow): SortableExpenseRow {
  if ("kind" in row && row.kind === "group") {
    return { description: row.label, interval: row.interval, amountInEuros: row.amountInEuros };
  }
  return row as SortableExpenseRow;
}

/**
 * What clicking a header does next: ascending, then descending, then back to the order it arrived in.
 *
 * THREE STATES BECAUSE THE THIRD ONE EXISTS. The server sorts by start date and then description —
 * "recently added first", which is what a member scanning for the thing they entered last month
 * wants — and a two-state toggle would make that order unreachable for the rest of the session once
 * any header was clicked. Clicking a DIFFERENT column starts it at ascending rather than inheriting
 * the previous column's direction, because a direction is a fact about a column and not about the
 * table.
 */
export function nextSort(
  current: ExpenseSort | null,
  column: ExpenseSortColumn,
): ExpenseSort | null {
  if (current === null || current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
}

/**
 * The rows in the order asked for, or exactly as they arrived when nothing was.
 *
 * A COPY, NEVER IN PLACE. `data.rows` belongs to the TanStack Query cache, and `Array.prototype.sort`
 * mutates — sorting it directly reorders the cached response, so the "unsorted" state would return to
 * whatever the last sort left behind rather than to the server's order, and a second component
 * reading the same cache entry would silently see the reordering too.
 *
 * THE SORT IS STABLE, which is what makes the tie-break correct without one being written: equal
 * values keep the server's own ordering, so ten monthly expenses sorted by interval stay in
 * start-date order underneath. `Array.prototype.sort` has been required to be stable since ES2019.
 */
export function sortRows<T extends SortableExpenseRow | SortableGroupRow>(
  rows: readonly T[],
  sort: ExpenseSort | null,
  /**
   * Each row's next due date (feature 024), for the one column whose value is not on the row: it comes from
   * the schedule read, so the page read's budget does not move. Absent means "no such column here".
   */
  nextDueOf?: (row: T) => string | null,
): T[] {
  if (sort === null) return [...rows];

  /*
   * NEXT DUE PUTS AN UNDATED ROW LAST IN BOTH DIRECTIONS (FR-028). Flipping the sign of a comparator that
   * puts null last would put it FIRST when descending, and a column sorted to answer "what goes out last?"
   * would open with every bill nobody has dated — the opposite of either question. So null is placed outside
   * the sign, and only the dates are reversed.
   */
  if (sort.column === "nextDue") {
    const due = (row: T) => nextDueOf?.(row) ?? null;
    const sign = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((left, right) => {
      const a = due(left);
      const b = due(right);
      if (a === b) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return sign * (a < b ? -1 : 1);
    });
  }

  const compare = COMPARATORS[sort.column];
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => sign * compare(sortable(left), sortable(right)));
}

type Comparator = (left: SortableExpenseRow, right: SortableExpenseRow) => number;

const COMPARATORS: Record<Exclude<ExpenseSortColumn, "nextDue">, Comparator> = {
  /**
   * BY THE READER'S LANGUAGE, not by code unit.
   *
   * `<` on strings compares UTF-16 code units, which puts every capital before every lowercase — so
   * "Zoo" sorts above "energie" — and files accented letters after `z`, which matters in a Dutch
   * household the first time somebody enters "Éclair" or a street name with a diaeresis.
   * `activeLocale()` is the same single source `format.ts` uses for how a figure is written; deriving
   * a locale separately here is the defect that suite already caught once.
   */
  description: (left, right) => left.description.localeCompare(right.description, activeLocale()),

  /**
   * BY DURATION, WHICH IS THE ONLY ORDER THAT MEANS ANYTHING.
   *
   * The obvious implementation compares the labels, and it produces "Every 4 weeks, Half-yearly,
   * Monthly, Quarterly, Weekly, Yearly" — six correct words in an order that says nothing, and which
   * changes completely in Dutch. Comparing the stored VALUES alphabetically is the same mistake one
   * layer down. `RECURRENCE_INTERVALS` is declared shortest-first in `@gladis/shared` and is the
   * list both this and income's picker already order by, so the index in it IS the ordering.
   *
   * An interval outside the list sorts last rather than throwing: this is a presentation comparator,
   * and a row the domain has already accepted must still be drawable.
   */
  interval: (left, right) => intervalRank(left.interval) - intervalRank(right.interval),

  /**
   * BY THE FIGURE IN THE CELL, and not by the monthly equivalent behind it.
   *
   * Decided deliberately, and the cost is real: €50.00 weekly is €216.67 a month and €200.00 yearly
   * is €16.67, so ascending by amount puts the cheaper commitment lower. The alternative sorts by
   * true cost and produces a money column whose figures read out of order — 2,400.00 sitting between
   * 45.00 and 50.00 — which a reader cannot check against anything on screen. This page's rule
   * everywhere else is that a displayed order is verifiable from what is displayed, and the interval
   * is in the column beside it for anyone comparing two rows in earnest.
   */
  amount: (left, right) => left.amountInEuros - right.amountInEuros,
};

function intervalRank(interval: string): number {
  const index = (RECURRENCE_INTERVALS as readonly string[]).indexOf(interval);
  return index === -1 ? RECURRENCE_INTERVALS.length : index;
}
